'use client';

import { type ReactElement } from 'react';
import { observer } from 'mobx-react-lite';
import { formatPrice } from '@fshr/shared';
import { useLocale, useTranslations } from '@fshr/context/i18n-context';
import PulsingDots from '../pulsing-dots/pulsing-dots';
import BevSafeText from '../bev-safe-text';
import type { CurrencySource, ProductAvailabilitySource } from './live-product-data';

type Props = {
	productId?: string;
	productAvailabilityStore?: ProductAvailabilitySource;
	userStore?: CurrencySource;
	complimentaryLabel?: string;
	className?: string;
};

function ExperiencePrice({
	productId,
	productAvailabilityStore,
	userStore,
	complimentaryLabel,
	className,
}: Props): ReactElement | null {
	const t = useTranslations();
	const locale = useLocale();
	const lineClass = ['text-body2 text-text-secondary', className].filter(Boolean).join(' ');
	const complimentaryClass = ['text-caption text-text-secondary', className]
		.filter(Boolean)
		.join(' ');

	const complimentary = complimentaryLabel ? (
		<p className={complimentaryClass}>
			<BevSafeText>{complimentaryLabel}</BevSafeText>
		</p>
	) : null;

	if (!productId) return complimentary;

	const products = productAvailabilityStore?.allProducts;
	if (!productAvailabilityStore || productAvailabilityStore.fetching) {
		return <PulsingDots className={`${lineClass} justify-start`} />;
	}

	const summary = products?.find(product => product.id === productId)?.summaries?.price;
	const from = summary?.from;
	if (!summary || summary.complimentary || !Number.isFinite(from?.amount)) return complimentary;

	const amount = formatPrice({
		value: from?.amount,
		currencyCode: userStore?.preferredCurrency || from?.currencyCode,
		locale,
		decimalPlaces: 0,
	});
	const unit = from?.type === 'PER_PERSON' ? ` /${t('person')}` : '';

	return (
		<p className={lineClass}>
			<span className="text-subtitle2">{t('Starting at')} </span>
			<span className="text-subtitle2 text-text-primary">{amount}</span>
			{unit ? <span className="text-caption text-text-primary">{unit}</span> : null}
		</p>
	);
}

export default observer(ExperiencePrice);
